import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../utils/notify";
import ApiLoader from "../../ui/ApiLoader";
import { DataTable } from "../../common/DataTable";
import { useAuth } from "../../../context/AuthContext";

import {
  fetchRules,
  fetchCombinedRules,
  fetchRuleById,
  createRule,
  updateRule,
  deleteRule,
  fetchAllRisks,
  fetchCustomerDataCheckById,
  createCustomerDataCheck,
  updateCustomerDataCheck,
} from "../../../services/axios/AuthApis/DashboardApis";

import {
  Eye,
  PenBoxIcon,
  Shield,
  X,
  ChevronDown,
  Search,
  Check,
  Trash2,
} from "lucide-react";

// Same searchable multi-select checklist pattern used by the CSPM
// Rules module's Risk Mapping section, restyled to match this
// module's purple/indigo modal theme.
const RiskMultiSelectDropdown = ({ riskList = [], selectedIds = [], onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const filteredRisks = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return riskList;

    return riskList.filter((risk) => {
      const id = String(risk.risk_id || "").toLowerCase();
      const name = String(risk.risk_name || risk.risk_description || "").toLowerCase();

      return id.includes(term) || name.includes(term);
    });
  }, [riskList, search]);

  const toggleRisk = (riskId) => {
    const next = selectedIds.includes(riskId)
      ? selectedIds.filter((id) => id !== riskId)
      : [...selectedIds, riskId];

    onChange(next);
  };

  const selectAll = () => {
    onChange([...new Set([...selectedIds, ...filteredRisks.map((r) => r.risk_id)])]);
  };

  const deselectAll = () => {
    const filteredIds = new Set(filteredRisks.map((r) => r.risk_id));
    onChange(selectedIds.filter((id) => !filteredIds.has(id)));
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-xl bg-gray-50 h-11 px-3 text-sm text-gray-900 focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={selectedIds.length ? "text-gray-900" : "text-gray-400"}>
          {selectedIds.length > 0
            ? `${selectedIds.length} Risk${selectedIds.length > 1 ? "s" : ""} Selected`
            : "Select Risks"}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by Risk ID or Risk Name"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 h-9 pl-9 pr-3 text-sm text-gray-900 focus:border-purple-400 focus:outline-none"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <button type="button" onClick={selectAll} className="font-medium text-purple-600 hover:underline">
                Select All
              </button>
              <button type="button" onClick={deselectAll} className="font-medium text-gray-500 hover:underline">
                Deselect All
              </button>
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredRisks.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">No risks found.</p>
            ) : (
              filteredRisks.map((risk) => {
                const checked = selectedIds.includes(risk.risk_id);

                return (
                  <div
                    key={risk.risk_id}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => toggleRisk(risk.risk_id)}
                    className="flex cursor-pointer items-start gap-3 px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        checked ? "border-purple-600 bg-purple-600" : "border-gray-300 bg-white"
                      }`}
                    >
                      {checked && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900">
                        {risk.risk_id} - {risk.risk_name || risk.risk_description}
                      </span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Rules = ({ activeTab }) => {
  const { isPlatformAdmin } = useAuth();

  const [rules, setRules] = useState([]);

  // Server-side pagination for the Platform Admin Rules table.
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Real backend-driven filters, same column_filters JSON + whitelist
  // scheme as Risk Register (_parse_check_column_filters on the backend).
  const [ruleFilters, setRuleFilters] = useState({ check_id: "", title: "", category: "", cloud_provider: "", severity: "", status: "", source: "" });
  const rulesRequestIdRef = useRef(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const [savingRule, setSavingRule] = useState(false);

  const [updatingRule, setUpdatingRule] = useState(false);

  const [showEditRuleModal, setShowEditRuleModal] = useState(false);


  const [tableLoading, setTableLoading] = useState(false);

  const [rulesLoading, setRulesLoading] = useState(false);

  const [showAddRuleModal, setShowAddRuleModal] = useState(false);

  // const [showViewRuleModal, setShowViewRuleModal] = useState(false);

  const [showViewModal, setShowViewModal] = useState(false);

  const [selectedRule, setSelectedRule] = useState(null);

  // Risk Management catalog for the Risk Mapping checklist -
  // customer admin only.
  const [riskList, setRiskList] = useState([]);

  const [customRiskInput, setCustomRiskInput] = useState("");

  const [editCustomRiskInput, setEditCustomRiskInput] = useState("");

  const [newRule, setNewRule] = useState({
    check_id: "",
    title: "",
    description: "",
    category: "",
    severity: "",
    cloud_provider: "",
    data_type: "pii",
    remediation_steps: "",
    is_active: true,
    risk_ids: [],
    custom_risks: [],
  });

  const [editRule, setEditRule] = useState({
    title: "",
    description: "",
    category: "",
    severity: "low",
    cloud_provider: "all",
    remediation_steps: "",
    risk_ids: [],
    custom_risks: [],
  });

  const loadRiskList = async () => {
    try {
      const response = await fetchAllRisks();
      setRiskList(Array.isArray(response) ? response : response?.data || []);
    } catch (err) {
      console.log(err);
      toast.error("Unable to load risks for mapping");
    }
  };

  const handleEditRule = async (rule) => {
    // Customer Admin: always load the freshest saved record from
    // CustomerDataCheck (not the possibly-stale table row) so the
    // Risk Mapping section reflects exactly what's in the DB.
    let record = rule;

    if (rule._source === "customer") {
      try {
        const response = await fetchCustomerDataCheckById(rule.id);
        record = { ...response.data, _source: "customer" };
      } catch (err) {
        console.log(err);
        toast.error("Unable to load rule for editing");
        return;
      }
    }

    setSelectedRule(record);

    setEditRule({
      title: record.title || "",
      description: record.description || "",
      category: record.category || "",
      severity: record.severity || "low",
      cloud_provider: record.cloud_provider || "all",
      remediation_steps: record.remediation_steps || "",
      risk_ids: record.risk_ids || record.mapped_risk_ids || [],
      custom_risks: record.custom_risks || [],
    });

    setEditCustomRiskInput("");

    setShowEditRuleModal(true);
  };

  const handleEditRuleChange = (e) => {
    const { name, value } = e.target;

    setEditRule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddEditCustomRisk = () => {
    const name = editCustomRiskInput.trim();

    if (!name) return;

    if (editRule.custom_risks.includes(name)) {
      setEditCustomRiskInput("");
      return;
    }

    setEditRule((prev) => ({
      ...prev,
      custom_risks: [...prev.custom_risks, name],
    }));

    setEditCustomRiskInput("");
  };

  const handleRemoveEditCustomRisk = (name) => {
    setEditRule((prev) => ({
      ...prev,
      custom_risks: prev.custom_risks.filter((r) => r !== name),
    }));
  };

  const handleUpdateRule = async () => {
    try {
      setUpdatingRule(true);

      // Customer Admin's rule (including its updated Risk Mapping)
      // is saved back into CustomerDataCheck. Platform Admin's
      // update path is unchanged.
      if (selectedRule._source === "customer") {
        await updateCustomerDataCheck(selectedRule.id, {
          ...editRule,
          mapped_risk_ids: editRule.risk_ids,
        });
      } else {
        await updateRule(selectedRule.check_id, editRule);
      }

      toast.success("Rule Updated Successfully");

      await loadRules(currentPage, pageSize, false);

      setShowEditRuleModal(false);
    } catch (err) {
      console.log(err);
      toast.error("Update Failed");
    } finally {
      setUpdatingRule(false);
    }
  };

  const loadRules = async (page = 1, size = pageSize, showLoader = true, filters = ruleFilters) => {
    const requestId = ++rulesRequestIdRef.current;
    try {
      if (showLoader) {
        setTableLoading(true);
      }

      const columnFilters = Object.entries(filters || {})
        .filter(([, value]) => value)
        .map(([id, value]) => ({ id, value }));
      const params = {
        page,
        page_size: size,
        ...(columnFilters.length ? { column_filters: JSON.stringify(columnFilters) } : {}),
      };

      // Platform admin: server-side paginated over DSPMCheck only.
      // Customer admin: server-side paginated over Platform rules
      // + their own CustomerDataCheck records, merged in the
      // backend so only one page is ever fetched at a time.
      const res = isPlatformAdmin
        ? await fetchRules(params)
        : await fetchCombinedRules(params);

      // A newer loadRules() call started since this one went out - its
      // response is stale even if it resolves last.
      if (requestId !== rulesRequestIdRef.current) return;

      const pageRules = (res.results || []).map((rule) => ({
        ...rule,
        _source: rule._source || "platform",
      }));

      setRules(pageRules);
      setCurrentPage(res.current_page || page);
      setTotalPages(res.total_pages || 1);
      setTotalCount(res.total_count || 0);
      setHasNext(!!res.has_next);
      setHasPrevious(!!res.has_previous);
      setPageSize(size);
    } catch (err) {
      if (requestId === rulesRequestIdRef.current) toast.error("Unable to load rules");
    } finally {
      if (showLoader && requestId === rulesRequestIdRef.current) {
        setTableLoading(false);
      }
    }
  };

  const handlePageChange = (page, size) => {
    loadRules(page, size);
  };

  useEffect(() => {
    if (activeTab === "rules") {
      const load = async () => {
        await loadRules(1);
        await loadRiskList();
      };

      load();
    }
  }, [activeTab, isPlatformAdmin]);

  // Re-run from page 1 whenever a header filter changes - same as Risk
  // Register's useEffect watching textColumnFilters.
  useEffect(() => {
    if (activeTab === "rules") {
      loadRules(1, pageSize, true, ruleFilters);
    }
  }, [ruleFilters]);

  const handleRuleChange = (e) => {
    const { name, value } = e.target;

    setNewRule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewRule = async (id, source = "platform") => {
    try {
      // Platform rules are looked up by check_id; CustomerDataCheck
      // records are looked up by their own record id.
      const response =
        source === "customer"
          ? await fetchCustomerDataCheckById(id)
          : await fetchRuleById(id);

      console.log("API Response:", response);

      setSelectedRule({ ...response.data, _source: source });

      setShowViewModal(true);
    } catch (err) {
      console.log(err);
    }
  };

  const handleDeleteRule = async (id) => {
    // const confirmDelete = window.confirm(
    //   "Are you sure you want to delete this rule?",
    // );
    // if (!confirmDelete) {
    //   return;
    // }
    try {
      await deleteRule(id);

      toast.success("Rule deleted");

      await loadRules(currentPage, pageSize, false);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleAddRule = async () => {
    //  const confirmSave = window.confirm(
    //    "Are you sure you want to save this rule?",
    //  );

    //  if (!confirmSave) {
    //    return;

    if (
      !newRule.check_id.trim() ||
      !newRule.title.trim() ||
      !newRule.category.trim() ||
      !newRule.cloud_provider ||
      !newRule.severity
    ) {
      toast.error("Check ID, Title and Category are required");
      return;
    }

    try {
      setSavingRule(true);

      // Platform admin saves into the Platform Rule collection
      // (DSPMCheck) as before. Customer admin's rule - including
      // its Risk Mapping (existing + custom risks) - is saved into
      // CustomerDataCheck instead, never into DSPMCheck.
      if (isPlatformAdmin) {
        await createRule(newRule);
      } else {
        await createCustomerDataCheck({
          ...newRule,
          mapped_risk_ids: newRule.risk_ids,
        });
      }

      toast.success("Rule Added Successfully");

      await loadRules(currentPage, pageSize, false);

      setShowAddRuleModal(false);

      // setRules((prev) => [...prev, response]);

      setNewRule({
        check_id: "",
        title: "",
        description: "",
        category: "",
        severity: "",
        cloud_provider: "",
        data_type: "pii",
        remediation_steps: "",
        is_active: true,
        risk_ids: [],
        custom_risks: [],
      });

      setCustomRiskInput("");
    } catch (err) {
      toast.error("Failed to Add Rule");
    } finally {
      setSavingRule(false);
    }
  };

  const handleAddCustomRisk = () => {
    const name = customRiskInput.trim();

    if (!name) return;

    if (newRule.custom_risks.includes(name)) {
      setCustomRiskInput("");
      return;
    }

    setNewRule((prev) => ({
      ...prev,
      custom_risks: [...prev.custom_risks, name],
    }));

    setCustomRiskInput("");
  };

  const handleRemoveCustomRisk = (name) => {
    setNewRule((prev) => ({
      ...prev,
      custom_risks: prev.custom_risks.filter((r) => r !== name),
    }));
  };

  const uniqueValues = (key) => [...new Set(rules.map((r) => r[key]).filter(Boolean))];

  const columns = [
    {
      id: "check_id",
      accessorFn: (r) => r.check_id,
      header: "Check ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Check ID", placeholder: "Search check ID" } },
      cell: ({ row }) => <span>{row.original.check_id}</span>,
    },
    {
      id: "title",
      accessorFn: (r) => r.title,
      header: "Title",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Title", placeholder: "Search title" } },
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      id: "category",
      accessorFn: (r) => r.category,
      header: "Category",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Category", placeholder: "All categories", options: uniqueValues("category").map((v) => ({ value: v, label: v })) } },
      cell: ({ row }) => <span>{row.original.category}</span>,
    },
    {
      id: "cloud_provider",
      accessorFn: (r) => r.cloud_provider,
      header: "Service",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Service", placeholder: "All services", options: uniqueValues("cloud_provider").map((v) => ({ value: v, label: v })) } },
      cell: ({ row }) => <span>{row.original.cloud_provider || "-"}</span>,
    },
    {
      id: "severity",
      accessorFn: (r) => r.severity,
      header: "Severity",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Severity",
          placeholder: "All severities",
          options: [
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
            { value: "critical", label: "Critical" },
          ],
        },
      },
      cell: ({ row }) => <span>{row.original.severity}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.is_active,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: [
            { value: "true", label: "Active" },
            { value: "false", label: "Disabled" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`px-3 py-1 rounded-full text-sm ${row.original.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {row.original.is_active ? "Active" : "Disabled"}
        </span>
      ),
    },
  ];

  if (!isPlatformAdmin) {
    columns.push({
      id: "source",
      accessorFn: (r) => r._source || "platform",
      header: "Source",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Source",
          placeholder: "All sources",
          options: [
            { value: "customer", label: "My Rule" },
            { value: "platform", label: "Platform" },
          ],
        },
      },
      cell: ({ row }) => (
        <span className={`px-3 py-1 rounded-full text-sm ${row.original._source === "customer" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
          {row.original._source === "customer" ? "My Rule" : "Platform"}
        </span>
      ),
    });
  }

  columns.push({
    id: "actions",
    header: "Actions",
    enableSorting: false,
    cell: ({ row }) => {
      const rule = row.original;
      return (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewRule(rule._source === "customer" ? rule.id : rule.check_id, rule._source)}
            className="border p-2 rounded text-blue-600"
          >
            <Eye size={18} />
          </button>
          {isPlatformAdmin && (
            <button onClick={() => handleEditRule(rule)} className="border p-2 rounded text-blue-600">
              <PenBoxIcon size={18} />
            </button>
          )}
          {isPlatformAdmin && (
            <button onClick={() => handleDeleteRule(rule.check_id)} className="border p-2 rounded text-red-600">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      );
    },
  });

  const columnFilters = Object.entries(ruleFilters)
    .filter(([, value]) => value)
    .map(([id, value]) => ({ id, value }));

  // Full-page loader only on the very first load (no rows yet) - a
  // filter-triggered refetch must not unmount the whole tab, only dim
  // the table (DataTable's own loading prop already handles that).
  if (tableLoading && rules.length === 0) {
    return (
      <ApiLoader variant="spinner" label="Loading Rules..." className="py-20" />
    );
  }

  return (
    <>
      {activeTab === "rules" && (
        <>
          <h2 className="text-2xl font-bold mb-6">Rules</h2>

          {/* Summary Cards */}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Total Rules</p>

              <h2 className="text-3xl font-bold mt-2">{totalCount}</h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Active Rules</p>

              <h2 className="text-3xl font-bold mt-2 text-green-600">
                {rules.filter((rule) => rule.is_active).length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Disabled Rules</p>

              <h2 className="text-3xl font-bold mt-2 text-red-600">
                {rules.filter((rule) => !rule.is_active).length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Critical Rules</p>

              <h2 className="text-3xl font-bold mt-2 text-orange-600">
                {
                  rules.filter(
                    (rule) =>
                      rule.severity === "critical" || rule.severity === "high",
                  ).length
                }
              </h2>
            </div>
          </div>

          {/* Rules Table */}

          <div className="bg-white rounded-xl shadow mt-6 p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold">Rules List</h2>

              <div className="flex gap-3">
                {/* <button
                  onClick={loadRules}
                  className="border px-4 py-2 rounded-lg"
                >
                  Refresh */}
                {/* </button> */}

                <button
                  onClick={() => setShowAddRuleModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                >
                  + Add Rule
                </button>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={rules}
              getRowId={(rule) => `${rule._source || "platform"}-${rule.id || rule.check_id}`}
              loading={tableLoading}
              emptyMessage="No Rules Found"
              manualPagination
              rowCount={totalCount}
              pagination={{ pageIndex: (currentPage || 1) - 1, pageSize }}
              onPaginationChange={(updater) => {
                const currentPagination = { pageIndex: (currentPage || 1) - 1, pageSize };
                const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
                loadRules(nextPagination.pageIndex + 1, nextPagination.pageSize);
              }}
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              manualFiltering
              columnFilters={columnFilters}
              onColumnFiltersChange={(updater) => {
                const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
                const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
                setRuleFilters({
                  check_id: values.check_id || "",
                  title: values.title || "",
                  category: values.category || "",
                  cloud_provider: values.cloud_provider || "",
                  severity: values.severity || "",
                  status: values.status || "",
                  source: values.source || "",
                });
              }}
              maxBodyHeight="65vh"
            />
          </div>

          {/* Add Rule Modal */}
          {showAddRuleModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative overflow-hidden max-h-[90vh] flex flex-col">
                {savingRule && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-50">
                    <ApiLoader variant="spinner" label="Saving Rule..." />
                  </div>
                )}

                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Add Rule</h2>
                      <p className="text-sm text-purple-100">
                        Fill in the rule details below
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowAddRuleModal(false)}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <div className="p-6 overflow-y-auto space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Check ID*
                      </label>
                      <input
                        type="text"
                        name="check_id"
                        placeholder="Check ID*"
                        value={newRule.check_id}
                        onChange={handleRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Title*
                      </label>
                      <input
                        type="text"
                        name="title"
                        placeholder="Title*"
                        value={newRule.title}
                        onChange={handleRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Description
                    </label>
                    <textarea
                      name="description"
                      placeholder="Description"
                      value={newRule.description}
                      onChange={handleRuleChange}
                      rows={3}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Category*
                      </label>
                      <select
                        name="category"
                        value={newRule.category}
                        onChange={handleRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      >
                        <option value="">Select Category</option>
                        <option value="data_exposure">Data Exposure</option>
                        <option value="encryption">Encryption</option>
                        <option value="access_control">Access Control</option>
                        <option value="retention">Data Retention</option>
                        <option value="classification">
                          Data Classification
                        </option>
                        <option value="compliance">Compliance</option>
                        <option value="data_loss_prevention">
                          Data Loss Prevention
                        </option>
                        <option value="public_storage">Public Storage</option>
                        <option value="misconfiguration">
                          Misconfiguration
                        </option>
                        <option value="identity_access">
                          Identity & Access Management
                        </option>
                        <option value="secrets_management">
                          Secrets Management
                        </option>
                        <option value="audit_logging">Audit Logging</option>
                        <option value="backup_recovery">
                          Backup & Recovery
                        </option>
                        <option value="network_security">
                          Network Security
                        </option>
                        <option value="malware_detection">
                          Malware Detection
                        </option>
                        <option value="vulnerability">
                          Vulnerability Management
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Severity*
                      </label>
                      <select
                        name="severity"
                        value={newRule.severity}
                        onChange={handleRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      >
                        <option value="">Select Severity</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Cloud Provider*
                    </label>
                    <select
                      name="cloud_provider"
                      value={newRule.cloud_provider}
                      onChange={handleRuleChange}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    >
                      <option value="">Cloud Provider</option>
                      <option value="aws">AWS</option>
                      <option value="azure">Azure</option>
                      <option value="gcp">GCP</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Remediation Steps
                    </label>
                    <textarea
                      name="remediation_steps"
                      placeholder="Remediation Steps"
                      value={newRule.remediation_steps}
                      onChange={handleRuleChange}
                      rows={4}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Risk Mapping */}
                  {
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Risk Mapping
                      </label>
                      <RiskMultiSelectDropdown
                        riskList={riskList}
                        selectedIds={newRule.risk_ids || []}
                        onChange={(ids) =>
                          setNewRule((prev) => ({ ...prev, risk_ids: ids }))
                        }
                        disabled={savingRule}
                      />

                      {/* Custom risks - not in the Risk Management catalog. Customer Admin only, since DSPMCheck has no custom_risks field. */}
                      {!isPlatformAdmin && (
                        <div className="mt-3">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Type a custom risk name..."
                              value={customRiskInput}
                              onChange={(e) =>
                                setCustomRiskInput(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCustomRisk();
                                }
                              }}
                              disabled={savingRule}
                              className="flex-1 border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={handleAddCustomRisk}
                              disabled={savingRule || !customRiskInput.trim()}
                              className="px-4 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Add Risk
                            </button>
                          </div>

                          {newRule.custom_risks.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {newRule.custom_risks.map((riskName) => (
                                <span
                                  key={riskName}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm"
                                >
                                  {riskName}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveCustomRisk(riskName)
                                    }
                                    className="hover:text-purple-900"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  }
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAddRuleModal(false)}
                    className="border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleAddRule}
                    disabled={
                      savingRule ||
                      !newRule.check_id.trim() ||
                      !newRule.title.trim() ||
                      !newRule.category.trim() ||
                      !newRule.cloud_provider ||
                      !newRule.severity
                    }
                    className={`px-5 py-2.5 rounded-xl font-medium text-white transition-colors ${
                      savingRule ||
                      !newRule.check_id.trim() ||
                      !newRule.title.trim() ||
                      !newRule.category.trim() ||
                      !newRule.cloud_provider ||
                      !newRule.severity
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {savingRule ? (
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Save Rule"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* view Rule Modal */}

          {showViewModal && selectedRule && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-[600px] max-h-[90vh] overflow-y-auto p-6">
                {" "}
                {/* <div className="flex justify-between items-center mb-5"> */}
                {/* <h2 className="text-xl font-bold">Rule Details</h2> */}
                {/* <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedRule(null);
                    }}
                    className="flex justify-end text-xl "
                  >
                    ✕
                  </button> */}
                {/* </div> */}
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-bold uppercase text-black mb-2">
                      Rule Description
                    </h3>

                    <div className="border rounded-xl p-4 text-white bg-blue-600">
                      {selectedRule.description}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Check ID
                      </p>
                      <p className="font-medium">{selectedRule.check_id}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Title</p>
                      <p className="font-medium">{selectedRule.title}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Category
                      </p>
                      <p>{selectedRule.category}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Severity
                      </p>

                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-600">
                        {selectedRule.severity}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Cloud Provider
                      </p>
                      <p>{selectedRule.cloud_provider}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Service</p>
                      <p>{selectedRule.service}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Data Type
                      </p>
                      <p>{selectedRule.data_type}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>

                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-600">
                        {selectedRule.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  {/* Remediation */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-gray-500 mb-2">
                      Remediation
                    </h3>

                    <div className="border rounded-xl p-4 bg-gray-50">
                      {selectedRule.remediation_steps}
                    </div>
                  </div>

                  {/* Risk Mapping - Customer Admin only */}
                  {(() => {
                    // Platform rules store the mapping in risk_ids;
                    // CustomerDataCheck records use mapped_risk_ids.
                    const mappedRiskIds =
                      selectedRule._source === "customer"
                        ? selectedRule.mapped_risk_ids
                        : selectedRule.risk_ids;

                    return (
                      <div>
                        <h3 className="text-sm font-semibold uppercase text-gray-500 mb-2">
                          Risk Mapping
                        </h3>

                        {(mappedRiskIds || []).length === 0 &&
                        (selectedRule.custom_risks || []).length === 0 ? (
                          <p className="text-gray-400 border rounded-xl p-4 bg-gray-50">
                            No risks mapped
                          </p>
                        ) : (
                          <div className="border rounded-xl bg-gray-50 divide-y divide-gray-200 max-h-56 overflow-y-auto">
                            {(mappedRiskIds || []).map((riskId) => {
                              const risk = riskList.find(
                                (r) => r.risk_id === riskId,
                              );

                              return (
                                <div key={riskId} className="p-3 text-sm">
                                  <span className="font-medium text-gray-900">
                                    {riskId}
                                  </span>
                                  {" - "}
                                  <span className="text-gray-600">
                                    {risk
                                      ? risk.risk_name || risk.risk_description
                                      : "Unknown Risk"}
                                  </span>
                                </div>
                              );
                            })}

                            {(selectedRule.custom_risks || []).map(
                              (riskName) => (
                                <div key={riskName} className="p-3 text-sm">
                                  <span className="font-medium text-gray-900">
                                    {riskName}
                                  </span>{" "}
                                  <span className="text-purple-600">
                                    (custom)
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedRule(null);
                    }}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Rule Modal */}

          {showEditRuleModal && selectedRule && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative overflow-hidden max-h-[90vh] flex flex-col">
                {updatingRule && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-50">
                    <ApiLoader variant="spinner" label="Updating Rule..." />
                  </div>
                )}

                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        Edit Rule
                      </h2>
                      <p className="text-sm text-purple-100">
                        Update the rule details below
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowEditRuleModal(false)}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <div className="p-6 overflow-y-auto space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Check ID
                      </label>
                      <input
                        type="text"
                        value={selectedRule.check_id || ""}
                        readOnly
                        className="w-full border-0 rounded-xl bg-gray-100 text-gray-500 p-3 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        placeholder="Title"
                        value={editRule.title}
                        onChange={handleEditRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Description
                    </label>
                    <textarea
                      name="description"
                      placeholder="Description"
                      value={editRule.description}
                      onChange={handleEditRuleChange}
                      rows={3}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Category
                      </label>
                      <input
                        type="text"
                        name="category"
                        placeholder="Category"
                        value={editRule.category}
                        onChange={handleEditRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Severity
                      </label>
                      <select
                        name="severity"
                        value={editRule.severity}
                        onChange={handleEditRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Cloud Provider
                      </label>
                      <select
                        name="cloud_provider"
                        value={editRule.cloud_provider}
                        onChange={handleEditRuleChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      >
                        <option value="all">All</option>
                        <option value="aws">AWS</option>
                        <option value="azure">Azure</option>
                        <option value="gcp">GCP</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Data Type
                      </label>
                      <input
                        type="text"
                        value={selectedRule.data_type || "-"}
                        readOnly
                        className="w-full border-0 rounded-xl bg-gray-100 text-gray-500 p-3 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Status
                    </label>
                    <span
                      className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium ${
                        selectedRule.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {selectedRule.is_active ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Remediation Steps
                    </label>
                    <textarea
                      name="remediation_steps"
                      placeholder="Remediation Steps"
                      value={editRule.remediation_steps}
                      onChange={handleEditRuleChange}
                      rows={4}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Risk Mapping - Customer Admin only. Platform
                      Admin's Edit flow now also supports the Risk
                      Mapping catalog dropdown (DSPMCheck.risk_ids),
                      but not free-text custom risks - those only
                      exist on CustomerDataCheck. */}
                  {
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Risk Mapping
                      </label>
                      <RiskMultiSelectDropdown
                        riskList={riskList}
                        selectedIds={editRule.risk_ids || []}
                        onChange={(ids) =>
                          setEditRule((prev) => ({ ...prev, risk_ids: ids }))
                        }
                        disabled={updatingRule}
                      />

                      {selectedRule._source === "customer" && (
                        <div className="mt-3">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Type a custom risk name..."
                              value={editCustomRiskInput}
                              onChange={(e) =>
                                setEditCustomRiskInput(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddEditCustomRisk();
                                }
                              }}
                              disabled={updatingRule}
                              className="flex-1 border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={handleAddEditCustomRisk}
                              disabled={
                                updatingRule || !editCustomRiskInput.trim()
                              }
                              className="px-4 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Add Risk
                            </button>
                          </div>

                          {editRule.custom_risks.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {editRule.custom_risks.map((riskName) => (
                                <span
                                  key={riskName}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm"
                                >
                                  {riskName}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveEditCustomRisk(riskName)
                                    }
                                    className="hover:text-purple-900"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  }
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowEditRuleModal(false)}
                    className="border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleUpdateRule}
                    disabled={updatingRule}
                    className={`px-5 py-2.5 rounded-xl font-medium text-white transition-colors ${
                      updatingRule
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {updatingRule ? (
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Update Rule"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Rules;
